import type * as H from 'history';
import queryString from 'query-string-for-all';
import type { ProductStatistics } from 'src/types';
import type {
  ISelectedDate,
  PriceTrendWithDate,
  ProductPriceHistoryState,
} from './types';

export const IMPORTANT_DATES = 'important-dates';
export const PRICE_TREND = 'price-trend';
export const PRICE_HISTORY = 'price-history';
export const MEDIAN_PRICE = 'median-price';
export const MAX_PRICE = 'max-price';

export type GraphType =
  | typeof IMPORTANT_DATES
  | typeof PRICE_TREND
  | typeof PRICE_HISTORY
  | typeof MEDIAN_PRICE
  | typeof MAX_PRICE;

export const queryStringGraph = {
  dates: IMPORTANT_DATES,
  price: PRICE_HISTORY,
  trend: PRICE_TREND,
  median: MEDIAN_PRICE,
  max: MAX_PRICE,
};

export function parseQueryStringForShare(
  location: H.Location,
  selectedGraphs: GraphType[],
  selectedStartDate: ISelectedDate
) {
  const qs = queryString.parse(location.search, {
    arrayFormat: 'comma',
  });

  qs.graphs = selectedGraphs?.map(
    g => Object.keys(queryStringGraph).find(key => queryStringGraph[key] === g)!
  );

  qs.period =
    selectedStartDate.period === 'all' ? undefined : selectedStartDate.period;

  return qs;
}

export function getDefaultStateForProductPriceHistory(): ProductPriceHistoryState {
  return {
    selectedPrices: {},
    expandedPrices: {},
    priceHistory: {},
    selectedStartDate: {
      name: undefined,
      min: new Date(1971).toISOString(),
      period: undefined,
    },
    // TODO: Due to the price trend data seemingly being broken with data missing, price trend is turned off per default for now.
    // selectedGraphs: [PRICE_HISTORY, PRICE_TREND],
    selectedGraphs: [PRICE_HISTORY],
    activeTabId: 'graph-tab',
  };
}

export const filterPriceTrends = (
  prices: PriceTrendWithDate[],
  minDate: Date
) => {
  const filteredPrices = (prices && prices.filter(p => p && p.date)) ?? [];

  if (!minDate) {
    return filteredPrices;
  }

  const startDate = new Date(minDate);

  return filteredPrices.filter(price => price.date >= startDate);
};

export const filterProductNodes = (
  prices: Pick<ProductStatistics, 'date' | 'lowestPrice'>[],
  minDate: Date
) => {
  const filteredPrices = (prices && prices.filter(p => p && p.date)) ?? [];

  if (!minDate) {
    return filteredPrices;
  }

  const startDate = new Date(minDate);

  return filteredPrices.filter(price => new Date(price!.date!) >= startDate);
};

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

// Calculate number of days between two dates.
export const daysBetweenDates = (
  date1: string | number | Date,
  date2: string | number | Date
) =>
  Math.round(
    Math.abs(
      (new Date(date1).getTime() - new Date(date2).getTime()) /
        MILLISECONDS_PER_DAY
    )
  );

// We only have one data point per month for mean prices. This function filter
// out dates before the start date and add a new point on the start date with
// correct data.
export const interpolateMeanPrice = (
  data: PriceTrendWithDate[],
  startDate: Date
) => {
  const before = data.filter(d => d && d.date && d.date < startDate);

  if (before.length === 0) {
    return data;
  }

  const after = data.filter(d => d && d.date && d.date >= startDate);

  // If after is empty and we are missing data, we will blow up, so returning early
  if (after.length === 0 || after[0] === null) {
    return [];
  }

  const lastFromBefore = before[before.length - 1];
  const originalDayDiff = daysBetweenDates(
    lastFromBefore!.date!,
    after[0].date!
  );
  const startDateDayDiff = daysBetweenDates(startDate, after[0].date!);

  const meanPricePerDay =
    (lastFromBefore!.meanPrice! - after[0].meanPrice!) / originalDayDiff;
  const lowestPricePerDay =
    (lastFromBefore!.lowestPrice! - after[0].lowestPrice!) / originalDayDiff;
  const highestPricePerDay =
    (lastFromBefore!.highestPrice! - after[0].highestPrice!) / originalDayDiff;

  return [
    {
      date: startDate,
      meanPrice: after[0].meanPrice! + meanPricePerDay * startDateDayDiff,
      lowestPrice: after[0].lowestPrice! + lowestPricePerDay * startDateDayDiff,
      highestPrice:
        after[0].highestPrice! + highestPricePerDay * startDateDayDiff,
    },
    ...after,
  ];
};

export const formatDate = (
  d:
    | number
    | Date
    | {
        valueOf(): number;
      }
) => {
  let date: Date;
  if (d instanceof Date) {
    date = d;
  } else if (typeof d === 'number') {
    date = new Date(d);
  } else {
    date = new Date(d.valueOf());
  }

  return `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(
    -2
  )}-${`0${date.getDate()}`.slice(-2)}`;
};
